import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { DashboardLayout } from "../components/DashboardLayout";
import { API } from "../lib/constants";

export default function DashboardHome() {
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState(null);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus("timeout");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPaymentStatus("success");
        refreshUser();
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      setPaymentStatus("error");
    }
  }, [refreshUser]);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const payment = searchParams.get("payment");
    if (payment === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.name}</h1>
          <p className="text-gray-600">Create amazing content with AI</p>
        </div>

        {paymentStatus === "success" && (
          <div className="bg-green-50 border border-green-500 text-green-700 p-4 mb-6" data-testid="payment-success">
            Payment successful! Your credits have been added.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-black p-6" data-testid="credits-card">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Credits</p>
            <p className="text-4xl font-black text-[#0066FF]">{user?.credits || 0}</p>
          </div>
          <div className="bg-white border border-black p-6" data-testid="plan-card">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Plan</p>
            <p className="text-2xl font-bold capitalize">{user?.subscription || "Free"}</p>
          </div>
          <div className="bg-white border border-black p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Text Cost</p>
            <p className="text-2xl font-bold">1 credit</p>
          </div>
          <div className="bg-white border border-black p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Image Cost</p>
            <p className="text-2xl font-bold">2 credits</p>
          </div>
        </div>

        <h2 className="text-xl font-bold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link to="/dashboard/text" className="bg-white border border-black p-6 hover:-translate-y-1 transition-transform hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="quick-text">
            <div className="w-12 h-12 bg-[#0066FF] text-white flex items-center justify-center font-black text-xl mb-4">T</div>
            <h3 className="text-xl font-bold mb-2">Generate Text Content</h3>
            <p className="text-gray-600">Create blog posts, ads, social media content, and emails</p>
          </Link>
          <Link to="/dashboard/image" className="bg-white border border-black p-6 hover:-translate-y-1 transition-transform hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="quick-image">
            <div className="w-12 h-12 bg-[#FF2A00] text-white flex items-center justify-center font-black text-xl mb-4">I</div>
            <h3 className="text-xl font-bold mb-2">Generate Images</h3>
            <p className="text-gray-600">Create stunning marketing visuals with AI</p>
          </Link>
        </div>

        {user?.credits < 5 && (
          <div className="mt-8 bg-[#0066FF]/10 border border-[#0066FF] p-6" data-testid="low-credits-banner">
            <h3 className="font-bold mb-2">Running low on credits?</h3>
            <p className="text-sm text-gray-600 mb-4">Upgrade your plan to keep creating amazing content.</p>
            <Link to="/pricing" className="inline-block bg-[#0066FF] text-white px-6 py-2 font-bold hover:bg-[#0052CC] transition-colors">Upgrade Now</Link>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
